import React from 'react';
import { createPortal } from 'react-dom';
import styled from '@emotion/styled';
import { Box, Flex, Text } from '@updater/ui-uds';
import {
  CompletedSegment,
  ValidatingSegment,
} from 'flows/core/components/cards/progress-bar-card/progress-bar-styles';
import { ProgressGate } from 'flows/core/components/cards/progress-bar-card/utils';
import { Direction } from '@updater/consumer-graph';
import { differenceInDays, parseISO, format, isValid } from 'date-fns';

const HeroSection = styled(Box)`
  background: ${({ theme }) => theme.whiteLabelTheme?.colors.heroBackground};
  overflow: hidden;
  position: relative;
`;

const HeroInner = styled(Box)`
  position: relative;
  padding: 24px 20px;
  margin-left: auto;
  margin-right: auto;
  width: 100%;
  box-sizing: border-box;

  ${({ theme }) => theme.mediaQueries.m} {
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 40px;
    min-height: 200px;
  }

  ${({ theme }) => theme.mediaQueries.l} {
    padding-left: ${({ theme }) => theme.space.m}px;
    padding-right: ${({ theme }) => theme.space.m}px;
    max-width: calc(720px + ${({ theme }) => theme.space.xxl}px);
  }
`;

const HeroTitle = styled(Text)<{ $isRenewal?: boolean }>`
  color: ${({ theme, $isRenewal }) =>
    $isRenewal
      ? theme.whiteLabelTheme?.colors.heroText ?? theme.colors.white
      : theme.colors.white};
  ${({ theme }) => {
    const fontFamily = theme.whiteLabelTheme?.fontFamily;
    if (!fontFamily) return '';
    return `font-family: ${fontFamily};`;
  }}
`;

const ChecklistText = styled.em`
  color: ${({ theme }) => theme.whiteLabelTheme?.colors.heroText};
  font-style: italic;
`;

const HeroSubtext = styled(Text)<{ $isRenewal?: boolean }>`
  color: ${({ theme, $isRenewal }) =>
    $isRenewal
      ? theme.whiteLabelTheme?.colors.heroText ?? theme.colors.white
      : theme.whiteLabelTheme?.colors.heroSubtext};
`;

const ProgressSection = styled(Box)`
  margin-top: 24px;
`;

const ProgressHeader = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const ProgressLabel = styled(Text)<{ $isRenewal?: boolean }>`
  color: ${({ theme, $isRenewal }) =>
    $isRenewal
      ? theme.whiteLabelTheme?.colors.heroText ?? theme.colors.white
      : theme.whiteLabelTheme?.colors.progressBarText};
  font-weight: 600;
  letter-spacing: 0.5px;
`;

const ProgressCount = styled(Text)<{ $isRenewal?: boolean }>`
  color: ${({ theme, $isRenewal }) =>
    $isRenewal
      ? theme.whiteLabelTheme?.colors.heroText ?? theme.colors.white
      : theme.whiteLabelTheme?.colors.progressBarText};
`;

const CompletedCount = styled(Text)<{ $isRenewal?: boolean }>`
  color: ${({ theme, $isRenewal }) =>
    $isRenewal
      ? theme.whiteLabelTheme?.colors.heroText ?? theme.colors.white
      : theme.colors.white};
`;

const ProgressBarContainer = styled(Box)`
  height: 8px;
  background: ${({ theme }) =>
    theme.whiteLabelTheme?.colors.progressBar ?? 'rgba(255,255,255,0.2)'};
  border-radius: 4px;
  overflow: hidden;
  position: relative;
`;

const RemainingText = styled(Text)`
  color: ${({ theme }) =>
    theme.whiteLabelTheme?.colors.heroText ?? theme.colors.white};
`;

interface WhiteLabelHeroProps {
  firstName?: string | null;
  /**
   * Every count comes from `getProgressGate`; this hero derives none itself.
   * Omitted only by callers that render no progress treatment at all.
   */
  progress?: ProgressGate;
  /**
   * Drives the copy. This hero serves every direction, so it needs the
   * direction itself — a move-out resident was previously told about their
   * "move-in checklist".
   */
  direction?: Direction | null;
  moveDate?: string | null;
  toAddress?: { street?: string | null; unit?: string | null } | null;
}

export const WhiteLabelHero: React.FC<WhiteLabelHeroProps> = ({
  firstName,
  progress,
  direction,
  moveDate,
  toAddress,
}) => {
  const {
    totalTasks = 0,
    displayedDone = 0,
    remaining = 0,
    completedPct = 0,
    validatingPct = 0,
    showProgressBar = false,
  } = progress ?? {};
  const isRenewal = direction === Direction.renewal;
  // Transfers and unknown directions read as a move-in, which is the closest
  // of the three and the long-standing default.
  const moveLabel = direction === Direction.move_out ? 'move-out' : 'move-in';
  const portalTarget = document.getElementById('white-label-hero-slot');

  let daysLeft: number | null = null;
  let formattedDate: string | null = null;
  if (isRenewal && moveDate) {
    const parsed = parseISO(moveDate);
    if (isValid(parsed)) {
      daysLeft = differenceInDays(parsed, new Date());
      formattedDate = format(parsed, 'MMMM do');
    }
  }

  const addressLine = isRenewal
    ? [toAddress?.street, toAddress?.unit].filter(Boolean).join(' ')
    : null;

  /**
   * With nothing to count there is no progress to report, so the whole progress
   * treatment is withheld rather than rendering an empty 0/0 bar. Matches the
   * `totalTasks === 0` guard in ProgressBarCard, which is the same bar on the
   * layouts that don't use this hero. This also suppresses the task-count
   * subtext, which would otherwise read "0 things before and after move-in day".
   */
  const hasTasks = showProgressBar;

  const hero = (
    <HeroSection
      height={['100%']}
      as="section"
      data-testid={isRenewal ? 'renewal-hero' : undefined}
    >
      <HeroInner>
        <HeroTitle
          $isRenewal={isRenewal}
          variant={['xlBold', 'xlBold', 'xxlBold', 'xxlBold', 'xxlBold']}
          mb="s"
          as="h1"
        >
          {isRenewal ? (
            <>
              {firstName ? `${firstName} – here's your` : "Here's your"} renewal
              checklist
            </>
          ) : (
            <>
              Your {moveLabel} <ChecklistText>checklist</ChecklistText>,{' '}
              {firstName}.
            </>
          )}
        </HeroTitle>

        {isRenewal
          ? (daysLeft !== null || addressLine) && (
              <HeroSubtext $isRenewal variant="s">
                {daysLeft !== null && daysLeft > 0 && formattedDate && (
                  <>
                    Only {daysLeft} days until your new lease {formattedDate}
                  </>
                )}
                {daysLeft === 0 && <>Your new lease starts today</>}
                {addressLine && (
                  <>
                    {' '}
                    for <strong>{addressLine}.</strong>
                  </>
                )}
              </HeroSubtext>
            )
          : hasTasks && (
              <HeroSubtext variant={['s', 's', 'm']}>
                {totalTasks} things before and after {moveLabel} day.
              </HeroSubtext>
            )}

        {hasTasks && (
          <ProgressSection>
            <ProgressHeader>
              <ProgressLabel $isRenewal={isRenewal} variant="xsBold">
                PROGRESS
              </ProgressLabel>
              <ProgressCount $isRenewal={isRenewal} variant="m">
                <CompletedCount
                  $isRenewal={isRenewal}
                  variant="lBold"
                  as="span"
                >
                  {displayedDone}
                </CompletedCount>{' '}
                /{totalTasks} done
              </ProgressCount>
            </ProgressHeader>
            <ProgressBarContainer>
              <Flex width="100%" height="100%">
                {completedPct > 0 && (
                  <CompletedSegment
                    widthPct={completedPct}
                    hasValidating={validatingPct > 0}
                    data-tooltip="Completed tasks"
                  />
                )}
                {validatingPct > 0 && (
                  <ValidatingSegment
                    widthPct={validatingPct}
                    hasCompleted={completedPct > 0}
                    data-tooltip="Tasks in review"
                  />
                )}
              </Flex>
            </ProgressBarContainer>
            {isRenewal && (
              <RemainingText variant="xs" mt="xs">
                &#x25CF; {remaining} remaining
              </RemainingText>
            )}
          </ProgressSection>
        )}
      </HeroInner>
    </HeroSection>
  );

  if (portalTarget) {
    return createPortal(hero, portalTarget);
  }

  return hero;
};
